import { AppState } from './app.service'
import { Injectable } from '@angular/core'
import { MESSAGES } from './locale/messages'

const PLACEHOLDER = /%\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g

@Injectable()
export class I18n {
  constructor(private appState: AppState) {
  }

  /**
   * The language to format dates and times in.
   *
   * Intl defaults to the BROWSER's locale when it is handed `undefined`, which is not the language
   * the learner picked in the app — it is why a Portuguese screen showed its weekdays in English.
   *
   * Checked once per language: an unusable tag would throw out of every date label on the screen,
   * and the check itself is too expensive to repeat for every row on every change-detection pass.
   */
  private checkedLocale: { lang: any; locale: string } | null = null;

  get locale(): string {
    const lang = this.appState.get('language') || 'en';
    if (this.checkedLocale && this.checkedLocale.lang === lang) { return this.checkedLocale.locale; }
    let locale = 'en';
    try {
      new Intl.DateTimeFormat(lang);
      locale = lang;
    } catch {
      // Not a language tag Intl knows. English reads better than a thrown error.
    }
    this.checkedLocale = { lang, locale };
    return locale;
  }

  t(value: any, args?: any): any {
    const rawLang = this.appState.get('language') || 'en'
    const lang = rawLang
    // ?.substr(0, 2)?.toLowerCase()

    if (lang != 'en' && MESSAGES[lang] && MESSAGES[lang][value]) {
      return this.interpolate(MESSAGES[lang][value], args)
    } else {
      return this.interpolate(value, args)
    }
  }

  tForLang(value: any, lang: string, args?: any): any {
    const normalizedLang = lang || 'en';
    // ?.slice(0, 2)?.toLowerCase() || 'en';
    const msgs = MESSAGES as any;
    if (normalizedLang !== 'en' && msgs[normalizedLang] && msgs[normalizedLang][value]) {
      return this.interpolate(msgs[normalizedLang][value], args);
    }
    return this.interpolate(value, args);
  }

  // Values are substituted after the catalog lookup, so the English source stays
  // the key and a translation is free to move the placeholder wherever that
  // language needs it. Callers that pass no args are unaffected.
  private interpolate(text: any, args?: any): any {
    if (!args || typeof text !== 'string') { return text }

    // An unfilled placeholder is left in place rather than blanked — a missing
    // value should be visible, not silently swallow part of the sentence.
    return text.replace(PLACEHOLDER, (match, key) =>
      args[key] === undefined || args[key] === null ? match : String(args[key])
    )
  }
}
